import { useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type {
  DashboardWidgetId,
  DashboardZoneId,
} from '@/products/helpdesk/features/dashboard/dashboard-layout';
import {
  getDashboardWidgetDef,
  getDashboardZoneDef,
} from '@/products/helpdesk/features/dashboard/dashboard-widget-registry';

export function DashboardCustomizeToolbar({
  editMode,
  onStartEdit,
  onFinishEdit,
  onReset,
}: {
  editMode: boolean;
  onStartEdit: () => void;
  onFinishEdit: () => void;
  onReset: () => void;
}) {
  const [resetOpen, setResetOpen] = useState(false);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {editMode ? (
          <>
            <Button type="button" variant="outline" size="sm" onClick={() => setResetOpen(true)}>
              Zurücksetzen
            </Button>
            <Button type="button" size="sm" onClick={onFinishEdit}>
              Fertig
            </Button>
          </>
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={onStartEdit}>
            Dashboard anpassen
          </Button>
        )}
      </div>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dashboard zurücksetzen?</AlertDialogTitle>
            <AlertDialogDescription>
              Zonen-Reihenfolge, ausgeblendete Bereiche und Widgets werden auf die Standardansicht
              zurückgesetzt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                onReset();
                setResetOpen(false);
              }}
            >
              Zurücksetzen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function DashboardHiddenPalette({
  hiddenZones,
  hiddenWidgets,
  onShowZone,
  onShowWidget,
}: {
  hiddenZones: DashboardZoneId[];
  hiddenWidgets: { zoneId: DashboardZoneId; widgetId: DashboardWidgetId }[];
  onShowZone: (zoneId: DashboardZoneId) => void;
  onShowWidget: (zoneId: DashboardZoneId, widgetId: DashboardWidgetId) => void;
}) {
  if (hiddenZones.length === 0 && hiddenWidgets.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4 rounded-xl border border-dashed px-4 py-4">
      {hiddenZones.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold tracking-tight">Ausgeblendete Zonen</h3>
          <ul className="flex flex-col gap-2">
            {hiddenZones.map((zoneId) => (
              <li
                key={zoneId}
                className="flex flex-wrap items-center justify-between gap-2 text-sm"
              >
                <span>{getDashboardZoneDef(zoneId).title}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onShowZone(zoneId)}
                >
                  Einblenden
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {hiddenWidgets.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold tracking-tight">Ausgeblendete Widgets</h3>
          <ul className="flex flex-col gap-2">
            {hiddenWidgets.map(({ zoneId, widgetId }) => (
              <li
                key={`${zoneId}:${widgetId}`}
                className="flex flex-wrap items-center justify-between gap-2 text-sm"
              >
                <span>
                  {getDashboardWidgetDef(widgetId).title}
                  <span className="text-muted-foreground">
                    {' '}
                    · {getDashboardZoneDef(zoneId).title}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onShowWidget(zoneId, widgetId)}
                >
                  Einblenden
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function DashboardEmptyLayout({
  onCustomize,
  onShowAllZones,
}: {
  onCustomize: () => void;
  onShowAllZones: () => void;
}) {
  return (
    <div role="status" className="rounded-xl border border-dashed px-4 py-8 text-center sm:px-6">
      <p className="text-base font-medium tracking-tight">Kein Bereich aktiv</p>
      <p className="mt-1 text-sm text-muted-foreground">
        Blenden Sie Arbeit oder IT-Lagebild wieder ein, um das Dashboard zu nutzen.
      </p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCustomize}>
          Dashboard anpassen
        </Button>
        <Button type="button" size="sm" onClick={onShowAllZones}>
          Zonen einblenden
        </Button>
      </div>
    </div>
  );
}

export function DashboardEmptyZoneHint({ onCustomize }: { onCustomize: () => void }) {
  return (
    <div role="status" className="rounded-lg border border-dashed px-4 py-6 text-center">
      <p className="text-sm text-muted-foreground">Keine Widgets aktiv — Anpassen öffnen.</p>
      <Button type="button" variant="link" className="mt-1 h-auto p-0" onClick={onCustomize}>
        Dashboard anpassen
      </Button>
    </div>
  );
}
